import type {
  EmployeeRoleLevel,
  WorkstreamBasicWithSummary,
  WorkstreamMetric,
  WorkstreamNavNode,
  WorkstreamUpdate,
} from 'client';
import {useMemo} from 'react';
import {getWorkstreamDescriptionPlainText} from 'views/workstreams/detail/workstreamDescriptionRichText';
import {getOutcomeStatus} from 'views/workstreams/detail/WorkstreamOutcomeStatus';
import {getFullWorkstreamMetrics} from 'views/workstreams/utils';

import {SUMMARIZE_MIN_CHARS} from './useUpdateSummary';

/**
 * /workstreams: what My workstreams last showed (the rail's trees
 * and the collapsed cards), so a return visit paints at once and the live data
 * replaces it as it lands, like the recents strip. Per person, versioned, a few
 * days at most, cleared on sign-out.
 *
 * Only what placing and drawing a collapsed card reads is kept: its name,
 * status, people, when and a short plain excerpt of its latest update (or its
 * summary), whether an update is due, and of its outcomes only the open,
 * non-MNPI ones' type, status, due date and owner (for Outcomes overdue). No
 * metric values or titles, descriptions, notes or full update text.
 */

const STORAGE_PREFIX = 'workstreams4_landing_v1:';
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
// At most the length shown as written, so a snapshot card never asks for an AI summary.
const EXCERPT_CHARS = SUMMARIZE_MIN_CHARS;

export type LandingSnapshot = {
  savedAt: number;
  // The half (table filter) it was saved for; another half doesn't use it.
  timeframe: string;
  navMy: WorkstreamNavNode[];
  navMyEnablement: WorkstreamNavNode[];
  navCompany: WorkstreamNavNode[];
  pillars: string[];
  narrativeLinks?: Record<string, {text: string; url: string}>;
  myHalf: WorkstreamBasicWithSummary[];
  myEnablement: WorkstreamBasicWithSummary[];
};

// Marks a card drawn from the snapshot, whose open body waits for the live row.
const SNAPSHOT_ROW = '_fromLandingSnapshot';

export const isSnapshotRow = (row: object): boolean =>
  Boolean((row as Record<string, unknown>)[SNAPSHOT_ROW]);

const storageKey = (ldap: string) => `${STORAGE_PREFIX}${ldap.toLowerCase()}`;

const trimPerson = <T extends EmployeeRoleLevel | null | undefined>(person: T): T =>
  (person && {
    user_id: person.user_id,
    ldap: person.ldap,
    name: person.name,
    role: person.role,
    level: person.level,
  }) as T;

const trimUpdate = (update: WorkstreamUpdate): WorkstreamUpdate => ({
  id: update.id,
  workstream_id: update.workstream_id,
  status: update.status,
  created_at: update.created_at,
  author: trimPerson(update.author),
  summary: update.summary,
  summary_edited: update.summary_edited,
  // A summary is what the card shows; without one, the start of the update.
  comment: update.summary
    ? ''
    : getWorkstreamDescriptionPlainText(update.comment).trim().slice(0, EXCERPT_CHARS),
});

// Open, non-MNPI milestones and deliverables, as far as Outcomes overdue reads them.
const trimOutcomes = (row: WorkstreamBasicWithSummary): WorkstreamMetric[] =>
  getFullWorkstreamMetrics(row.metrics)
    .filter(
      (metric) =>
        metric.type !== 'metric' &&
        !metric.is_mnpi &&
        getOutcomeStatus({complete: metric.complete, status: metric.status}) === 'open'
    )
    .map((metric) => ({
      id: metric.id,
      type: metric.type,
      visibility: metric.visibility,
      complete: metric.complete,
      status: metric.status,
      complete_date: metric.complete_date,
      owner: trimPerson(metric.owner),
      is_mnpi: false,
      title: '',
      created_at: '',
      updated_at: '',
      current: null,
      target: null,
      url: null,
      is_longterm: null,
    }));

export const trimCardRow = (row: WorkstreamBasicWithSummary): WorkstreamBasicWithSummary => ({
  id: row.id,
  name: row.name,
  archived: row.archived,
  privacy: row.privacy,
  pillar: row.pillar,
  start_date: row.start_date,
  end_date: row.end_date,
  status_due: row.status_due,
  status_cadence: row.status_cadence,
  status_due_date: row.status_due_date,
  status_update_needed: row.status_update_needed,
  dependencies_count: row.dependencies_count,
  employee_associations: row.employee_associations.map((association) => ({
    dri: association.dri,
    employee: trimPerson(association.employee),
  })),
  updates: row.updates?.[0] ? [trimUpdate(row.updates[0])] : [],
  metrics: trimOutcomes(row),
});

const markSnapshotRows = (rows: WorkstreamBasicWithSummary[]) =>
  rows.map((row) => ({...row, [SNAPSHOT_ROW]: true}));

export const loadLandingSnapshot = (
  ldap: string | null | undefined,
  timeframe: string,
  now: number = Date.now()
): LandingSnapshot | null => {
  if (!ldap) return null;
  try {
    const key = storageKey(ldap);
    // Anyone else's (a session that ended without signing out) goes.
    const others = Array.from({length: localStorage.length}, (_, index) => localStorage.key(index));
    others
      .filter(
        (other): other is string => Boolean(other?.startsWith(STORAGE_PREFIX)) && other !== key
      )
      .forEach((other) => localStorage.removeItem(other));
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const snapshot = JSON.parse(stored) as LandingSnapshot;
    if (now - snapshot.savedAt > MAX_AGE_MS) {
      // Too old to show, so not kept either.
      localStorage.removeItem(key);
      return null;
    }
    if (snapshot.timeframe !== timeframe) return null;
    return {
      ...snapshot,
      myHalf: markSnapshotRows(snapshot.myHalf),
      myEnablement: markSnapshotRows(snapshot.myEnablement),
    };
  } catch {
    // Unreadable (or storage unavailable): drop it.
    try {
      localStorage.removeItem(storageKey(ldap));
    } catch {
      /* storage unavailable */
    }
    return null;
  }
};

/** Save what the page just loaded (trimmed), replacing anyone else's on this browser. */
export const saveLandingSnapshot = (
  ldap: string | null | undefined,
  snapshot: Omit<LandingSnapshot, 'savedAt'>,
  now: number = Date.now()
) => {
  if (!ldap) return;
  try {
    clearLandingSnapshots();
    localStorage.setItem(
      storageKey(ldap),
      JSON.stringify({
        ...snapshot,
        savedAt: now,
        myHalf: snapshot.myHalf.map(trimCardRow),
        myEnablement: snapshot.myEnablement.map(trimCardRow),
      } satisfies LandingSnapshot)
    );
  } catch {
    /* storage unavailable or full: next visit just loads */
  }
};

/** Drop every saved snapshot (sign-out). */
export const clearLandingSnapshots = () => {
  try {
    const keys = Array.from({length: localStorage.length}, (_, index) => localStorage.key(index));
    keys
      .filter((key): key is string => Boolean(key?.startsWith(STORAGE_PREFIX)))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    /* storage unavailable */
  }
};

/**
 * Your snapshot for this half. Read once you're known: on a fresh load the
 * page mounts before /auth/me returns, so nothing shows until then, and it's
 * only ever yours.
 */
export const useLandingSnapshot = (ldap: string | null | undefined, timeframe: string) =>
  useMemo(() => loadLandingSnapshot(ldap, timeframe), [ldap, timeframe]);
